import React, { useEffect, useState } from "react";
import "./index.css";
import { Content } from "antd/es/layout/layout";
import HeaderDocumentAnalysis from "../../assets/img/documentAnalysis.png";
import FileUpload from "../../assets/img/file-upload-icon.png";
import RemoveIcon from "../../assets/img/remove-icon.png";
import {
  Button,
  message,
  Modal,
  Progress,
  Radio,
  Input,
  Form,
  Tooltip,
  Spin,
} from "antd";
import { get, postWithHeaders } from "../../utils/apiUtils";
import { useTranslation } from "react-i18next";
import useDrivePicker from "react-google-drive-picker";
import { initGoogleDriveClient } from "../../services/googleDriveService";
import { gapi } from "gapi-script";
import {
  HeaderDataType,
  LanguageType,
  ReadabilityType,
} from "../../types/types";
import DocumentModals from "./DocumentsModal";
import FileUploadImage from "../../assets/img/fileUpload.png";
import { parseEntities } from "../../hooks/entitiesForDocsFiles";

const { TextArea } = Input;

// KONSTANTA PËR MAX FILE SIZE
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function GoogleDrive() {
  const { t } = useTranslation("googleDrive");
  const [fileList, setFileList] = useState<any>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false); // For download modal
  const [processedFileURL, setProcessedFileURL] = useState<string>(""); // Store processed file URL
  const [language, setLanguage] = useState("");
  // const [tokenizationType, setTokenizationType] = useState<string>(""); // For tokenization input
  const [stopWordsEnabled, setStopWordsEnabled] = useState<boolean>(false);
  const [supportedLanguages, setSupportedLanguages] = useState<LanguageType[]>(
    []
  );
  const [stemOrLemmatize, setStemOrLemmatize] = useState<string>("");
  const [previewURL, setPreviewURL] = useState<string>("");
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openPicker, authResponse] = useDrivePicker();
  const [summarize, setSummarize] = useState<string>("false");
  const [sentimentAnalysis, setSentimentAnalysis] = useState<string>("false");
  const [enableReadability, setReadability] = useState<string>("false");
  const [enableEntities, setExtractEntities] = useState<string>("false");
  const [enableGrammarCheck, setEnableGrammarCheck] = useState<string>("false");
  const [tokenizationType, setTokenizationType] = useState<string>("false");
  const [isDriveFile, setIsDriveFile] = useState(false);
  const [fileExt, setFileExt] = useState("");

  const [headerData, setHeaderData] = useState<HeaderDataType>({
    title: "",
    tokenizationType: "",
    summaryIncluded: "",
    wordCount: "",
    language: "",
    date: "",
    numPages: 0,
    sentiment: "",
    readability: {
      fleschReadingScore: 0,
      gunningFogIndex: 0,
      smogIndex: 0,
    },
    entities: [],
  });

  useEffect(() => {
    if (typeof gapi !== "undefined") {
      initGoogleDriveClient();
    } else {
      console.error("Google API client not loaded.");
    }
  }, []);

  const createPreviewURL = (file: File) => {
    const fileURL = window.URL.createObjectURL(file);
    setPreviewURL(fileURL);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const file = files[0];

      // KONTROLLI I MADHËSISË SË FILE-IT (25MB)
      if (file.size > MAX_FILE_SIZE_BYTES) {
        message.error(t("messages.fileSizeExceeds", { size: MAX_FILE_SIZE_MB }));
        return;
      }

      const newFile = {
        uid: file.name,
        name: file.name,
        status: "done",
        originFileObj: file,
      };
      setFileList([newFile]);
      setUploadProgress(100);
      createPreviewURL(file);
      setIsDriveFile(false);
      const ext = file.name.split(".").pop()?.toLowerCase() || "";
      setFileExt(ext);
      setPreviewURL(URL.createObjectURL(file)); // kjo punon vetëm për PDF

      handleModalCancel();
    }
  };

  // const handleOpenPreviewModal = () => {
  //   if (fileList.length === 0) {
  //     message.error("No file to preview");
  //     return;
  //   }

  //   const selectedFile = fileList[0];

  //   if (selectedFile.url) {
  //     // Google Drive file, use its embed URL for preview
  //     setPreviewURL(
  //       `https://docs.google.com/document/d/${selectedFile.uid}/preview`
  //     );
  //   }

  //   // Open preview modal
  //   setIsPreviewModalVisible(true);
  // };

  const handleOpenPreviewModal = () => {
    if (fileList.length === 0) {
      message.error(t("messages.noFileToPreview"));
      return;
    }

    if (!isDriveFile && fileExt === "docx") {
      message.info(t("messages.docxPreviewUnavailable"));
      return;
    }

    const selectedFile = fileList[0];

    if (isDriveFile && selectedFile.url) {
      // Google Drive preview
      setPreviewURL(
        `https://docs.google.com/document/d/${selectedFile.uid}/preview`
      );
    }

    setIsPreviewModalVisible(true);
  };

  const handlePreviewModalCancel = () => {
    setIsPreviewModalVisible(false);
  };

  const handleProcessClick = async () => {
    setLoading(true);
    const formData = new FormData();

    if (fileList.length === 0) {
      message.error(t("messages.noFileSelected"));
      setLoading(false);
      return;
    }

    const selectedFile = fileList[0];

    // Kontrollo nëse është file nga Google Drives
    if (selectedFile.url) {
      const googleAuthToken = authResponse?.access_token;

      if (!googleAuthToken) {
        message.error(t("messages.googleAuthFailed"));
        setLoading(false);
        return;
      }

      formData.append("google_drive_file_id", selectedFile.uid);
      formData.append("file", "");
      formData.append("google_auth_code", googleAuthToken);
    } else {
      formData.append("file", selectedFile.originFileObj);
      formData.append("google_drive_file_id", "");
    }

    formData.append("summarize", summarize === "true" ? "true" : "false");
    formData.append(
      "sentiment_analysis",
      sentimentAnalysis === "true" ? "true" : "false"
    );
    formData.append(
      "readability_analysis",
      enableReadability === "true" ? "true" : "false"
    );
    formData.append(
      "entity_extraction",
      enableEntities === "true" ? "true" : "false"
    );
    formData.append(
      "grammar_check",
      enableGrammarCheck === "true" ? "true" : "false"
    );

    formData.append(
      "tokenize",
      tokenizationType === "false" ? "false" : "true"
    );
    formData.append("tokenize_by", tokenizationType);
    formData.append(
      "custom_delim",
      tokenizationType === "custom" ? "your_custom_delim" : ""
    );
    formData.append("stem", stemOrLemmatize === "stemming" ? "true" : "false");
    formData.append(
      "lemmatize",
      stemOrLemmatize === "lemmatization" ? "true" : "false"
    );
    formData.append("remove_stopwords", stopWordsEnabled ? "true" : "false");
    formData.append("language", language);

    try {
      // Thirrja API
      // const response = await postWithHeaders("preprocess", formData, {
      //   headers: {
      //     "Content-Type": "multipart/form-data",
      //   },
      // });
      const response = await postWithHeaders("preprocess", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          const total = progressEvent.total || 1;
          const percent = Math.round((progressEvent.loaded * 100) / total);
          setUploadProgress(percent);
        },
      });

      if (response.type === "success") {
        const { data, headers } = response;

        const contentType = headers["content-type"] || "";

        if (
          contentType.includes(
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          )
        ) {
          // If the response is a document file, generate a download link
          const fileURL = window.URL.createObjectURL(new Blob([data]));
          setProcessedFileURL(fileURL);
          setIsDownloadModalVisible(true);
          message.success(t("messages.processSuccess"));

          const entities = headers["x-entities"];
          const readability = headers["x-readability"];
          const sentiment = headers["x-sentiment"];

          let parsedEntities = [];
          let parsedReadability: ReadabilityType = {
            fleschReadingScore: 0,
            gunningFogIndex: 0,
            smogIndex: 0,
          };

          if (readability) {
            if (readability === "Not available") {
              console.warn(
                "Readability is not available. Setting default values."
              );
            } else if (typeof readability === "string") {
              try {
                const fixedReadability = readability.replace(/'/g, '"');

                const tempReadability = JSON.parse(fixedReadability);

                parsedReadability = {
                  fleschReadingScore: tempReadability.flesch_reading_ease || 0,
                  gunningFogIndex: tempReadability.gunning_fog || 0,
                  smogIndex: tempReadability.smog_index || 0,
                };
              } catch (error) {
                message.error(t("messages.readabilityParseFailed"));
              }
            } else {
              console.warn(
                "Readability header exists but is not a valid string."
              );
            }
          }

          try {
            parsedEntities = parseEntities(entities);
            console.log("Parsed entities:", parsedEntities);
          } catch (error) {
            console.error("Error parsing entities:", error);
            message.error(t("messages.entitiesParseFailed"));
          }
          setHeaderData({
            title: headers["x-title"] || "N/A",
            tokenizationType: headers["x-tokenization-type"] || "N/A",
            summaryIncluded:
              headers["x-summary-included"] === "True"
                ? t("downloadModal.yes")
                : t("downloadModal.no"),
            wordCount: headers["x-word-count"] || "N/A",
            language: headers["x-language"] || "N/A",
            date: headers["x-processed-at"] || "N/A",
            numPages: parseInt(headers["x-num-pages"] || "0", 10),
            sentiment: headers["x-sentiment"] || "UNKNOWN",
            readability: parsedReadability,
            entities: parsedEntities,
          });
        }
      } else {
        message.error(t("messages.processError"));
      }
    } catch (error: any) {
      console.error("API Call Error:", error);
      message.error(t("messages.processErrorDetail", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };
  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleDownloadModalCancel = () => {
    setIsDownloadModalVisible(false);
    setLoading(false);
  };

  useEffect(() => {
    const fetchSupportedLanguages = async () => {
      try {
        const response = await get<any>("languages/", {});
        setSupportedLanguages(response.data as LanguageType[]);
      } catch (error) {
        console.error(
          "There was an error fetching the supported languages:",
          error
        );
      }
    };

    fetchSupportedLanguages();
  }, []);

  // const handleOpenPicker = async () => {
  //   try {
  //     const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "ID_JUYT";
  //     const API_KEY = process.env.REACT_APP_API_KEY || "";

  //     openPicker({
  //       clientId: CLIENT_ID,
  //       developerKey: API_KEY,
  //       viewId: "DOCS",
  //       showUploadView: true,
  //       showUploadFolders: true,
  //       supportDrives: true,
  //       multiselect: false,
  //       callbackFunction: (data) => {
  //         if (data.action === "picked") {
  //           const doc = data.docs[0];

  //           // KONTROLL PËR MADHËSINË E FILE-IT NGA GOOGLE DRIVE (NËSE KA sizeBytes)
  //           if (doc.sizeBytes && doc.sizeBytes > MAX_FILE_SIZE_BYTES) {
  //             message.error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
  //             return;
  //           }

  //           const selectedGoogleDriveFile = {
  //             uid: doc.id,
  //             name: doc.name,
  //             status: "done",
  //             url: doc.url,
  //           };

  //           setFileList([selectedGoogleDriveFile]);
  //           handleModalCancel();
  //         } else {
  //           console.log("User clicked cancel/close button");
  //         }
  //       },
  //     });
  //   } catch (e) {
  //     console.error("Error opening Google Drive picker", e);
  //     message.error("Could not open Google Drive picker.");
  //   }
  // };

  const handleOpenPicker = async () => {
    try {
      const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "ID_JUYT";
      const API_KEY = process.env.REACT_APP_API_KEY || "";

      openPicker({
        clientId: CLIENT_ID,
        developerKey: API_KEY,
        viewId: "DOCS",
        showUploadView: true,
        showUploadFolders: true,
        supportDrives: true,
        multiselect: false,
        callbackFunction: (data) => {
          if (data.action === "picked") {
            const doc = data.docs[0];

            // size check
            if (doc.sizeBytes && doc.sizeBytes > MAX_FILE_SIZE_BYTES) {
              message.error(t("messages.fileSizeExceeds", { size: MAX_FILE_SIZE_MB }));
              return;
            }

            const ext = doc.name?.split(".").pop()?.toLowerCase() || "";

            const selectedGoogleDriveFile = {
              uid: doc.id,
              name: doc.name,
              status: "done",
              url: doc.url, // ekziston vetëm për Drive
            };

            setIsDriveFile(true); // ⭐ E RËNDËSISHME
            setFileExt(ext); // ⭐ ruaje extension-in
            setFileList([selectedGoogleDriveFile]);

            // Preview URL për Drive (docs.google.com)
            setPreviewURL(
              `https://docs.google.com/document/d/${doc.id}/preview`
            );

            handleModalCancel();
          } else {
            console.log("User clicked cancel/close button");
          }
        },
      });
    } catch (e) {
      console.error("Error opening Google Drive picker", e);
      message.error(t("messages.pickerError"));
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setTokenizationType("");
    setStopWordsEnabled(false);
    setUploadProgress(0);
    setStemOrLemmatize("");
    setSummarize("");
    setSentimentAnalysis("");
    setReadability("");
    setExtractEntities("");
    setEnableGrammarCheck("");
    setLoading(false);
  };
  return (
    <div className="user-management-layout" style={{ marginLeft: 0 }}>
      <Content className="content-styles">
        <div className="header-image">
          <img
            src={HeaderDocumentAnalysis}
            alt="document analysis"
            style={{ width: "100%" }}
          />
        </div>

        <div className="card">
          <div className="upload-document-style">
            <h2>{t("page.uploadHeading")}</h2>
            <div className="upload-file-section">
              <img
                className="img-style"
                src={FileUploadImage}
                alt="upload file"
              />
              <Button onClick={showModal} className="uploadButtonStyle">
                <span
                  style={{ textDecoration: "underline", marginLeft: "3px" }}
                >
                  {t("page.selectFiles")}
                </span>
              </Button>
              <span
                style={{
                  color: "#737373",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {t("page.wordOrPdf")}
              </span>
              <span
                style={{
                  color: "#737373",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {t("page.maxFileSize", { size: MAX_FILE_SIZE_MB })}
              </span>
            </div>

            <Modal
              title={t("chooseSourceModal.title")}
              visible={isModalVisible}
              onCancel={handleModalCancel}
              footer={null}
              className="download-modal-style"
            >
              <div style={{ display: "flex", justifyContent: "space-around" }}>
                <Button
                  onClick={handleOpenPicker}
                  className="drive-files-style"
                >
                  {t("chooseSourceModal.selectFromDrive")}
                </Button>
                <Button
                  type="default"
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = ".docx";
                    input.onchange = (event: any) => {
                      handleFileUpload(
                        event as React.ChangeEvent<HTMLInputElement>
                      );
                    };
                    input.click();
                  }}
                  className="mycomputer-files-style"
                >
                  {t("chooseSourceModal.selectFromComputer")}
                </Button>
              </div>
            </Modal>
            {/* Selected Files List */}
            {fileList.length > 0 && (
              <div className="selected-file-list" style={{ marginTop: "20px" }}>
                {fileList.map((file: any) => (
                  <div key={file.uid} className="file-container">
                    <div className="file-inner-container">
                      <img
                        src={FileUpload}
                        alt="test"
                        style={{ height: "44px", width: "44px" }}
                      />

                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{file.size}</span>
                    </div>

                    <img
                      src={RemoveIcon}
                      alt="remove"
                      onClick={() => setFileList([])}
                      style={{ width: "24px", height: "24px" }}
                    />
                  </div>
                ))}
                <Progress percent={uploadProgress} status="active" />
              </div>
            )}

            <Modal
              title={t("twoOptionsModal.title")}
              visible={isModalVisible}
              onCancel={handleModalCancel}
              footer={null}
              className="download-modal-style"
              style={{ width: "604px" }}
            >
              <p>
                {t("twoOptionsModal.description")}
              </p>
              <div style={{ display: "flex", justifyContent: "space-around" }}>
                <button
                  onClick={() => handleOpenPicker()}
                  className="drive-files-style"
                >
                  {t("twoOptionsModal.selectFromDrive")}
                </button>

                <Button
                  type="default"
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = ".docx";

                    input.onchange = (event: Event) => {
                      const target = event.target as HTMLInputElement;
                      if (target.files && target.files.length > 0) {
                        handleFileUpload({
                          target,
                        } as React.ChangeEvent<HTMLInputElement>);
                      }
                    };

                    input.click(); // This opens the file selection dialog
                  }}
                  className="mycomputer-files-style"
                >
                  {t("twoOptionsModal.selectFromComputer")}
                </Button>
              </div>
            </Modal>
          </div>

          <div className="main-content">
            <h3>{t("preprocessing.heading")}</h3>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label={t("preprocessing.summarize.label")}></Form.Item>

                <div className="general-content">
                  <div className="radio-divv">
                    <Radio
                      checked={summarize === "true"}
                      onClick={() =>
                        setSummarize((prevValue) =>
                          prevValue === "true" ? "" : "true"
                        )
                      }
                      className="custom-radio"
                    >
                      {t("preprocessing.summarize.yes")}
                    </Radio>
                  </div>
                  <div className="radio-divv">
                    <Radio
                      checked={summarize === "false"}
                      onClick={() =>
                        setSummarize((prevValue) =>
                          prevValue === "false" ? "" : "false"
                        )
                      }
                      className="custom-radio"
                    >
                      {t("preprocessing.summarize.no")}
                    </Radio>
                  </div>
                </div>
              </div>
            </div>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label={t("preprocessing.tokenization.label")}></Form.Item>
                <div>
                  <div className="tokenization-style">
                    <Tooltip title={t("preprocessing.tokenization.byWordTooltip")}>
                      <div className="radio-divv">
                        <Radio
                          checked={tokenizationType === "word"}
                          onClick={() =>
                            setTokenizationType((prevValue) =>
                              prevValue === "word" ? "" : "word"
                            )
                          }
                          className="custom-radio"
                        >
                          {t("preprocessing.tokenization.byWord")}
                        </Radio>
                      </div>
                    </Tooltip>
                    <Tooltip title={t("preprocessing.tokenization.bySentenceTooltip")}>
                      <div className="radio-divv">
                        <Radio
                          checked={tokenizationType === "sentence"}
                          onClick={() =>
                            setTokenizationType((prevValue) =>
                              prevValue === "sentence" ? "" : "sentence"
                            )
                          }
                          className="custom-radio"
                        >
                          {t("preprocessing.tokenization.bySentence")}
                        </Radio>
                      </div>
                    </Tooltip>
                    <Tooltip title={t("preprocessing.tokenization.customTooltip")}>
                      <div className="radio-divv">
                        <Radio
                          checked={tokenizationType === "custom"}
                          onClick={() =>
                            setTokenizationType((prevValue) =>
                              prevValue === "custom" ? "" : "custom"
                            )
                          }
                          className="custom-radio"
                        >
                          {t("preprocessing.tokenization.custom")}
                        </Radio>
                      </div>
                    </Tooltip>
                  </div>
                </div>

                {tokenizationType === "custom" && (
                  <div
                    style={{
                      background: "#FCF3FF",
                      padding: "1rem",
                      marginTop: "1rem  ",
                    }}
                  >
                    <h4 className="h4" style={{ marginTop: "unset" }}>
                      {t("preprocessing.tokenization.customDelimiterHeading")}
                    </h4>
                    <TextArea
                      placeholder={t("preprocessing.tokenization.customPlaceholder")}
                      autoSize={{ minRows: 2, maxRows: 20 }}
                    />{" "}
                  </div>
                )}
              </div>
            </div>

            <div className="tokenization-content">
              <h4 className="h4">{t("preprocessing.stemLemma.heading")}</h4>
              <div>
                <div className="general-content">
                  <Tooltip title={t("preprocessing.stemLemma.stemmingTooltip")}>
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={stemOrLemmatize === "stemming"}
                        onClick={() =>
                          setStemOrLemmatize((prevValue) =>
                            prevValue === "stemming" ? "" : "stemming"
                          )
                        }
                      >
                        {t("preprocessing.stemLemma.stemming")}
                      </Radio>
                    </div>
                  </Tooltip>
                  <Tooltip title={t("preprocessing.stemLemma.lemmatizationTooltip")}>
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={stemOrLemmatize === "lemmatization"}
                        onClick={() =>
                          setStemOrLemmatize((prevValue) =>
                            prevValue === "lemmatization" ? "" : "lemmatization"
                          )
                        }
                      >
                        {t("preprocessing.stemLemma.lemmatization")}
                      </Radio>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="tokenization-content">
              <h4 className="h4">{t("preprocessing.stopWords.heading")}</h4>
              <div className="radio-divv">
                <Radio
                  checked={stopWordsEnabled}
                  className="custom-radio"
                  onClick={() => setStopWordsEnabled((prevValue) => !prevValue)}
                >
                  {t("preprocessing.stopWords.enable")}
                </Radio>
              </div>

              {stopWordsEnabled && (
                <div
                  style={{
                    background: "#FCF3FF",
                    padding: "1rem",
                    marginTop: "1rem  ",
                  }}
                >
                  <h4 className="h4" style={{ marginTop: "unset" }}>
                    {t("preprocessing.stopWords.writeHeading")}
                  </h4>
                  <TextArea
                    placeholder={t("preprocessing.stopWords.placeholder")}
                    autoSize={{ minRows: 2, maxRows: 20 }}
                  />{" "}
                </div>
              )}
            </div>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label={t("preprocessing.sentiment.label")}></Form.Item>
                <div className="general-content">
                  <Tooltip title={t("preprocessing.sentiment.enableTooltip")}>
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={sentimentAnalysis === "true"}
                        onClick={() =>
                          setSentimentAnalysis((prevValue) =>
                            prevValue === "true" ? "" : "true"
                          )
                        }
                      >
                        {t("preprocessing.sentiment.enable")}
                      </Radio>
                    </div>
                  </Tooltip>

                  <Tooltip title={t("preprocessing.sentiment.disableTooltip")}>
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={sentimentAnalysis === "false"}
                        onClick={() =>
                          setSentimentAnalysis((prevValue) =>
                            prevValue === "false" ? "" : "false"
                          )
                        }
                      >
                        {t("preprocessing.sentiment.disable")}
                      </Radio>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label={t("preprocessing.readability.label")}></Form.Item>
                <div className="general-content">
                  <Tooltip
                    className="readability-tooltip"
                    title={
                      <div className="readAbility-style-tooltip">
                        <p>
                          <strong>{t("preprocessing.readability.tooltip.purposeLabel")}</strong>{" "}
                          {t("preprocessing.readability.tooltip.purpose")}
                        </p>
                        <p>
                          <strong>{t("preprocessing.readability.tooltip.fleschLabel")}</strong>
                        </p>
                        <p>
                          {t("preprocessing.readability.tooltip.fleschDef")}
                        </p>
                        <ul>
                          <li>
                            <strong>{t("preprocessing.readability.tooltip.flesch90to100Label")}</strong>{" "}
                            {t("preprocessing.readability.tooltip.flesch90to100")}
                          </li>
                          <li>
                            <strong>{t("preprocessing.readability.tooltip.flesch60to70Label")}</strong>{" "}
                            {t("preprocessing.readability.tooltip.flesch60to70")}
                          </li>
                          <li>
                            <strong>{t("preprocessing.readability.tooltip.flesch0to30Label")}</strong>{" "}
                            {t("preprocessing.readability.tooltip.flesch0to30")}
                          </li>
                        </ul>
                        <p>
                          <strong>{t("preprocessing.readability.tooltip.gunningLabel")}</strong>
                        </p>
                        <p>
                          {t("preprocessing.readability.tooltip.gunningDef")}
                        </p>
                        <ul>
                          <li>
                            <strong>{t("preprocessing.readability.tooltip.gunning6to10Label")}</strong>{" "}
                            {t("preprocessing.readability.tooltip.gunning6to10")}
                          </li>
                          <li>
                            <strong>{t("preprocessing.readability.tooltip.gunningAbove17Label")}</strong>{" "}
                            {t("preprocessing.readability.tooltip.gunningAbove17")}
                          </li>
                        </ul>
                        <p>
                          <strong>{t("preprocessing.readability.tooltip.smogLabel")}</strong>
                        </p>
                        <p>
                          {t("preprocessing.readability.tooltip.smogDef")}
                        </p>
                        <ul>
                          <li>
                            {t("preprocessing.readability.tooltip.smogNote")}
                          </li>
                        </ul>
                      </div>
                    }
                    placement="right"
                  >
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={enableReadability === "true"}
                        onClick={() =>
                          setReadability((prevValue) =>
                            prevValue === "true" ? "" : "true"
                          )
                        }
                      >
                        {t("preprocessing.readability.enable")}
                      </Radio>
                    </div>
                  </Tooltip>
                  <div className="radio-divv">
                    <Radio
                      className="custom-radio"
                      checked={enableReadability === "false"}
                      onClick={() =>
                        setReadability((prevValue) =>
                          prevValue === "false" ? "" : "false"
                        )
                      }
                    >
                      {t("preprocessing.readability.disable")}
                    </Radio>
                  </div>
                </div>
              </div>
            </div>

            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label={t("preprocessing.entities.label")}></Form.Item>
                <div className="general-content">
                  <Tooltip title={t("preprocessing.entities.tooltip")}>
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={enableEntities === "true"}
                        onClick={() =>
                          setExtractEntities((prevValue) =>
                            prevValue === "true" ? "" : "true"
                          )
                        }
                      >
                        {t("preprocessing.entities.enable")}
                      </Radio>
                    </div>
                  </Tooltip>
                  <div className="radio-divv">
                    <Radio
                      className="custom-radio"
                      checked={enableEntities === "false"}
                      onClick={() =>
                        setExtractEntities((prevValue) =>
                          prevValue === "false" ? "" : "false"
                        )
                      }
                    >
                      {t("preprocessing.entities.disable")}
                    </Radio>
                  </div>
                </div>
              </div>
            </div>
            <div className="tokenization-content">
              <div className="first-div-style">
                <Form.Item label={t("preprocessing.grammar.label")}></Form.Item>
                <div className="general-content">
                  <Tooltip title={t("preprocessing.grammar.tooltip")}>
                    <div className="radio-divv">
                      <Radio
                        className="custom-radio"
                        checked={enableGrammarCheck === "true"}
                        onClick={() =>
                          setEnableGrammarCheck((prevValue) =>
                            prevValue === "true" ? "" : "true"
                          )
                        }
                      >
                        {t("preprocessing.grammar.enable")}
                      </Radio>
                    </div>
                  </Tooltip>
                  <div className="radio-divv">
                    <Radio
                      className="custom-radio"
                      checked={enableGrammarCheck === "false"}
                      onClick={() =>
                        setEnableGrammarCheck((prevValue) =>
                          prevValue === "false" ? "" : "false"
                        )
                      }
                    >
                      {t("preprocessing.grammar.disable")}
                    </Radio>
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "1rem" }}
            >
              <button onClick={handleCancel} className="cancelButtonStyle">
                {t("preprocessing.cancel")}
              </button>
              <button
                onClick={handleProcessClick}
                className="processButtonStyle"
              >
                {loading ? <Spin /> : t("preprocessing.process")}
              </button>{" "}
            </div>
          </div>
        </div>
        <DocumentModals
          isDownloadModalVisible={isDownloadModalVisible}
          isPreviewModalVisible={isPreviewModalVisible}
          handleDownloadModalCancel={handleDownloadModalCancel}
          handlePreviewModalCancel={handlePreviewModalCancel}
          handleOpenPreviewModal={handleOpenPreviewModal}
          processedFileURL={processedFileURL}
          headerData={headerData}
          previewURL={previewURL}
        />
      </Content>
    </div>
  );
}

export default GoogleDrive;
